import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUserPlus, FiSearch, FiFilter, FiMoreVertical, FiShield, FiUserX, FiMail, FiChevronDown } = FiIcons;

const mockUsers = [
  { id: 1, name: 'Dr. William Gray', email: 'wgray@stloralsurgery.com', avatar: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=40&h=40&fit=crop&crop=face', role: 'superadmin', lastActive: '2024-07-21', status: 'Active' },
  { id: 2, name: 'Dr. Sarah Mitchell', email: 'sarah.mitchell@example.com', avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=40&h=40&fit=crop&crop=face', role: 'provider', lastActive: '2024-07-20', status: 'Active' },
  { id: 3, name: 'Admin User', email: 'admin@example.com', avatar: 'https://ui-avatars.com/api/?name=Admin+User', role: 'admin', lastActive: '2024-07-21', status: 'Active' },
  { id: 4, name: 'Patient Zero', email: 'patient.zero@example.com', avatar: 'https://ui-avatars.com/api/?name=Patient+Zero', role: 'patient', lastActive: '2024-07-18', status: 'Invited' },
  { id: 5, name: 'Community Member', email: 'community@example.com', avatar: 'https://ui-avatars.com/api/?name=Community+Member', role: 'member', lastActive: '2024-07-19', status: 'Active' },
  { id: 6, name: 'Dr. Michael Chen', email: 'michael.chen@example.com', avatar: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=40&h=40&fit=crop&crop=face', role: 'provider', lastActive: '2024-07-20', status: 'Active' },
];

const roles = ['superadmin', 'admin', 'provider', 'patient', 'member'];
const roleColors = {
  superadmin: 'bg-red-100 text-red-800',
  admin: 'bg-purple-100 text-purple-800',
  provider: 'bg-blue-100 text-blue-800',
  patient: 'bg-green-100 text-green-800',
  member: 'bg-gray-100 text-gray-800',
};

const UserRoleManagement = () => {
  const [users, setUsers] = useState(mockUsers);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const handleRoleChange = (userId, newRole) => {
    setUsers(users.map(user => user.id === userId ? { ...user, role: newRole } : user));
  };

  const filteredUsers = users
    .filter(user => user.name.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(user => roleFilter === 'all' || user.role === roleFilter);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Role Management</h1>
            <p className="text-gray-600 mt-1">Manage user permissions and access levels.</p>
          </div>
          <button className="mt-4 sm:mt-0 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
            <SafeIcon icon={FiUserPlus} className="w-4 h-4" />
            <span>Add User</span>
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative md:col-span-2">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                value={roleFilter}
                onChange={e => setRoleFilter(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="all">All Roles</option>
                {roles.map(role => <option key={role} value={role} className="capitalize">{role}</option>)}
              </select>
              <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <AnimatePresence>
                  {filteredUsers.map(user => (
                    <motion.tr
                      key={user.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <img className="h-10 w-10 rounded-full" src={user.avatar} alt={user.name} />
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={user.role}
                          onChange={e => handleRoleChange(user.id, e.target.value)}
                          className={`px-2 py-1 text-sm font-medium rounded-md appearance-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 capitalize ${roleColors[user.role]}`}
                        >
                          {roles.map(role => <option key={role} value={role}>{role}</option>)}
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(user.lastActive).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        <button className="text-gray-400 hover:text-gray-600 p-1">
                          <SafeIcon icon={FiMoreVertical} className="w-5 h-5" />
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default UserRoleManagement;